import { canAccessAdminSection, canAccessUserCreation } from '@/utils/adminRoles';
import { LinkItemProps } from '../types/linkItemProps';
import { getLinkItems } from './getLinkItems';
import { getLinkItemsForCorporateAdmin } from './getLinkItemsForCorporateAdmin';
import { getLinkItemsForSuperAdmin } from './getLinkItemsForSuperAdmin';
import { linkItemsForCSUser, linkItemsForCSLTIUser } from './linkItemsForCSUser';
import { linkItemsForSubscriptionManager } from './linkItemsForSubscriptionManager';
import { LinkItemsForPublishUser } from './linkItemsForPublishUser';

type FeatureFlags = Record<string, boolean>;

const normalizeUrl = (url: string) => {
    if (url.length > 1 && url.endsWith('/')) {
        return url.slice(0, -1);
    }
    return url;
};

const mergeLinkItems = (items: LinkItemProps[]): LinkItemProps[] => {
    const byUrl = new Map<string, LinkItemProps>();

    items.forEach((item) => {
        const key = normalizeUrl(item.url);
        const existing = byUrl.get(key);

        if (!existing) {
            byUrl.set(key, item);
            return;
        }

        // Prefer a visible item when the same link appears with different hide flags.
        if (existing.hide && !item.hide) {
            byUrl.set(key, item);
        }
    });

    return Array.from(byUrl.values());
};

export const getLinkItemsForRoles = (roles: string[] = [], featureFlags: FeatureFlags = {}): LinkItemProps[] => {
    const roleSet = new Set(roles);
    const items: LinkItemProps[] = [];

    // Higher-privilege menus first so shared items keep their primary ordering.
    if (roleSet.has('ROLE_SUPER_ADMIN')) {
        items.push(...getLinkItemsForSuperAdmin(featureFlags));
    }
    if (roleSet.has('ROLE_CORPORATE_ADMIN')) {
        items.push(...getLinkItemsForCorporateAdmin(featureFlags));
    }
    if (roleSet.has('ROLE_ADMIN')) {
        items.push(...getLinkItems(featureFlags));
    }
    if (roleSet.has('ROLE_SUBSCRIPTION_MANAGER')) {
        items.push(...linkItemsForSubscriptionManager);
    }
    if (roleSet.has('ROLE_CS_USER')) {
        items.push(...linkItemsForCSUser);
    }
    if (roleSet.has('ROLE_CS_LTI_USER')) {
        items.push(...linkItemsForCSLTIUser);
    }
    if (roleSet.has('ROLE_PUBLISH')) {
        items.push(...LinkItemsForPublishUser);
    }

    const merged = mergeLinkItems(items);

    if (canAccessAdminSection(roles) || !canAccessUserCreation(roles)) {
        return merged;
    }

    const usersLink: LinkItemProps = { name: 'Users', icon: 'id-card', url: '/admins/users-list' };
    const withoutAdminSection = merged.filter((item) => normalizeUrl(item.url) !== '/admins/list');
    const institutionIndex = withoutAdminSection.findIndex((item) => normalizeUrl(item.url) === '/institutions/list');

    if (institutionIndex === -1) {
        return [...withoutAdminSection, usersLink];
    }

    const withUsersLink = [...withoutAdminSection];
    withUsersLink.splice(institutionIndex + 1, 0, usersLink);
    return withUsersLink;
};
